/* eslint-disable sort-imports */
// leaflet must be imported before geoman so that window.L is set when geoman's IIFE executes
import L from "leaflet";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import "./leafletCustom.css";

import {
    COORDINATE_DECIMAL_COUNT,
    DYNAMIC_FEATURE_ID_FIELD_NAME,
} from "../constants";
import { useEffect, useRef } from "react";

import { PARCELLAIRE_REDUCER_ACTIONS } from "../parcellaireReducer";
import { deleteLayers } from "../utils";
import { roundNumber } from "../../utils/roundNumber";
import { useFormContext } from "react-hook-form";
import { useMap } from "react-leaflet";

export const useGeomanConfig = ({
    parcellaireDispatch,
    parcellaireStateRef,
    setCoordMarkerMode,
    setGeoJsonKey,
    onSoilDrawRef,
    setIsDrawingSoil,
    soilPolygonsRef,
    onRadioMetricTargetDrawRef,
    setIsDrawingRadioMetricTarget,
    radioMetricTargetPolygonRef,
    radioMetricTargetName,
    zoomToOrthoimage,
    isRotatingRef,
    isRulerActive,
    setIsRulerActive,
    openScaleDialog,
    setOpenScaleDialog,
}) => {
    const map = useMap();
    const { reset, setValue } = useFormContext();
    const currentDrawingTypeRef = useRef(null);

    // Track ruler state via ref for synchronous reads in event handlers
    const isRulerActiveRef = useRef(isRulerActive);
    isRulerActiveRef.current = isRulerActive;

    // Use ref to avoid re-running effect when setIsRulerActive changes
    const setIsRulerActiveRef = useRef(setIsRulerActive);
    setIsRulerActiveRef.current = setIsRulerActive;

    // Use ref to access latest name
    const radioMetricTargetNameRef = useRef(radioMetricTargetName);
    radioMetricTargetNameRef.current = radioMetricTargetName;
    // Use a ref for zoomToOrthoimage to avoid stale closures
    // while preventing the effect from re-running when the function changes
    const zoomToOrthoimageRef = useRef(zoomToOrthoimage);
    zoomToOrthoimageRef.current = zoomToOrthoimage;

    const setOpenScaleDialogRef = useRef(setOpenScaleDialog);
    setOpenScaleDialogRef.current = setOpenScaleDialog;

    // Track scale dialog state via ref so onKeyDown can tell "our" scale
    // dialog apart from a foreign modal (save/export/metadata) without
    // being blocked by its own presence in the DOM.
    const openScaleDialogRef = useRef(openScaleDialog);
    openScaleDialogRef.current = openScaleDialog;

    useEffect(() => {
        map.pm.Toolbar.createCustomControl({
            name: "markerReset",
            block: "draw",
            title: "Removes all line markers",
            className: "leaflet-pm-icon-delete",
            onClick: () => {
                deleteLayers(
                    map,
                    (layer) => layer.options.customType === "marker"
                );
            },
            afterClick: () => {
                map.pm.enableGlobalDragMode();
            },
            toggle: false,
        });

        map.pm.Toolbar.copyDrawControl("Polygon", {
            name: POLYGON_SELECT_NAME,
            block: "draw",
            title: "Select (polygon)",
            onClick: () => {
                if (map.pm.globalDragModeEnabled()) {
                    map.pm.disableGlobalDragMode();
                }
            },
            afterClick: (_, { button }) => {
                map.pm.Draw[POLYGON_SELECT_NAME].toggle();

                if (!button.toggled()) {
                    map.pm.enableGlobalDragMode();
                }
            },
            actions: [],
        });

        map.pm.Toolbar.createCustomControl({
            name: "polygonLock",
            block: "draw",
            title: "Lock selection",
            className: "leaflet-pm-icon-lock",
            onClick: () => {
                parcellaireDispatch({
                    type: PARCELLAIRE_REDUCER_ACTIONS.LOCK,
                });
                setGeoJsonKey(new Date().getTime());
            },
            afterClick: () => {
                map.pm.enableGlobalDragMode();
            },
            toggle: false,
        });

        map.pm.Toolbar.createCustomControl({
            name: "polygonUnlock",
            block: "draw",
            title: "Unlock all",
            className: "leaflet-pm-icon-unlock",
            onClick: () => {
                parcellaireDispatch({
                    type: PARCELLAIRE_REDUCER_ACTIONS.UNLOCK,
                });
                setGeoJsonKey(new Date().getTime());
            },
            afterClick: () => {
                map.pm.enableGlobalDragMode();
            },
            toggle: false,
        });

        map.pm.Toolbar.createCustomControl({
            name: "unselect",
            block: "edit",
            title: "Save checkpoint & Unselect",
            className: "leaflet-pm-icon-save",
            onClick: () => {
                parcellaireDispatch({
                    type: PARCELLAIRE_REDUCER_ACTIONS.CHECKPOINT,
                });
                setGeoJsonKey(new Date().getTime());
            },
            afterClick: () => {
                map.pm.enableGlobalDragMode();
            },
            toggle: false,
        });

        map.pm.Toolbar.createCustomControl({
            name: "softReset",
            block: "edit",
            title: "Reset to last checkpoint",
            className: "leaflet-pm-icon-soft-reset",
            onClick: () => {
                parcellaireDispatch({
                    type: PARCELLAIRE_REDUCER_ACTIONS.GO_TO_CHECKPOINT,
                });
                setGeoJsonKey(new Date().getTime());
            },
            afterClick: () => {
                map.pm.enableGlobalDragMode();
            },
            toggle: false,
        });

        map.pm.Toolbar.createCustomControl({
            name: "orthoimageZoom",
            block: "edit",
            title: "Zoom to Orthoimage",
            className: "leaflet-pm-icon-zoom",
            onClick: () => {
                zoomToOrthoimageRef.current?.();
            },
            afterClick: () => {
                map.pm.enableGlobalDragMode();
            },
            toggle: false,
        });

        map.pm.Toolbar.createCustomControl({
            name: "polygonDelete",
            block: "options",
            title: "Delete selection",
            className: "leaflet-pm-icon-trash",
            onClick: () => {
                deleteLayers(
                    map,
                    (layer) => layer.options.customType === "idLabel"
                );
                parcellaireDispatch({
                    type: PARCELLAIRE_REDUCER_ACTIONS.DELETE,
                });
                setGeoJsonKey(new Date().getTime());
            },
            afterClick: () => {
                map.pm.enableGlobalDragMode();
            },
            toggle: false,
        });

        const onCreateHandler = (e) => {
            if (e.shape === POLYGON_SELECT_NAME) {
                // If there is a selection
                const geoJsonSelect = e.layer.toGeoJSON();

                const allLayers = map.pm
                    .getGeomanLayers()
                    .filter(
                        (layer) =>
                            layer.feature?.geometry.type !== "Point" &&
                            layer.feature?.properties?.[
                                DYNAMIC_FEATURE_ID_FIELD_NAME
                            ] !== undefined
                    );

                parcellaireDispatch({
                    type: PARCELLAIRE_REDUCER_ACTIONS.SELECT,
                    e,
                    geoJsonSelect,
                    allLayers,
                });

                setGeoJsonKey(new Date().getTime());

                e.layer.remove();
                map.pm.disableDraw();
            } else if (e.shape === "Polygon") {
                setGeoJsonKey(new Date().getTime());
                if (currentDrawingTypeRef.current === "soil") {
                    e.layer.options.customType = "soil";
                    e.layer.bindPopup("Name : soil");

                    soilPolygonsRef.current = e.layer;
                    setIsDrawingSoil(false);
                } else if (
                    currentDrawingTypeRef.current === "radioMetricTarget"
                ) {
                    e.layer.options.customType = "radioMetricTarget";
                    const radioMetricTargetText =
                        radioMetricTargetNameRef.current
                            ? `Radiometric Target: ${radioMetricTargetNameRef.current}`
                            : "Radiometric Target <br/>Final name will be set when saving";
                    e.layer.bindPopup(radioMetricTargetText);

                    if (radioMetricTargetPolygonRef) {
                        radioMetricTargetPolygonRef.current = e.layer;
                    }
                    setIsDrawingRadioMetricTarget(false);
                }

                currentDrawingTypeRef.current = null;
            } else if (e.shape === "Marker") {
                const { lat, lng } = e.layer.getLatLng();
                // If this is a coordinate marker
                setCoordMarkerMode(false);
                setValue(
                    "latitude",
                    roundNumber(lat, COORDINATE_DECIMAL_COUNT),
                    {
                        shouldDirty: true,
                    }
                );
                setValue(
                    "longitude",
                    roundNumber(lng, COORDINATE_DECIMAL_COUNT),
                    {
                        shouldDirty: true,
                    }
                );
                reset((formValues) => ({ ...formValues }));
            }
            map.pm.enableGlobalDragMode();
        };
        map.on("pm:create", onCreateHandler);

        // Track rotation mode so the pm:edit handler in MapLayout can skip
        // translation vector creation during rotation.
        // Since geoman-free lacks per-layer disableRotate(), we intercept the
        // global toggle, temporarily mark non-allowed layers as pmIgnore, then
        // re-enable so geoman only attaches rotation handles to allowed layers.
        // We keep pmIgnore set while rotation is active and use a layeradd
        // listener so that layers created by re-renders also get ignored.
        let rotateGuard = false;
        let rotateAllowedIds = null;
        // Whether rotation was started with an explicit selection. When
        // false (no selection), only the grabbed plot should rotate, even
        // though every unlocked plot keeps a rotation handle available.
        let rotateHasSelection = false;

        const isLayerAllowedForRotation = (layer) => {
            if (!rotateAllowedIds || !layer.feature) return false;
            const featureId =
                layer.feature.properties?.[DYNAMIC_FEATURE_ID_FIELD_NAME];
            return rotateAllowedIds.has(featureId);
        };

        const applyRotateIgnore = (layer) => {
            if (!layer.options) return;
            if (!isLayerAllowedForRotation(layer)) {
                layer.options.pmIgnore = true;
            }
        };

        const clearRotateIgnore = () => {
            map.eachLayer((layer) => {
                if (layer.options) {
                    layer.options.pmIgnore = false;
                }
            });
            rotateAllowedIds = null;
        };

        const onLayerAdd = (e) => {
            if (rotateAllowedIds) {
                applyRotateIgnore(e.layer);
            }
        };

        map.on("layeradd", onLayerAdd);

        map.on("pm:globalrotatemodetoggled", (e) => {
            if (rotateGuard) return;
            isRotatingRef.current = e.enabled;

            if (e.enabled) {
                rotateGuard = true;
                map.pm.disableGlobalRotateMode();

                const currentState = parcellaireStateRef.current;
                const selectedIds = new Set(
                    currentState.selectedLayers.map(
                        (l) =>
                            l.feature.properties[DYNAMIC_FEATURE_ID_FIELD_NAME]
                    )
                );
                const lockedIds = new Set(
                    currentState.lockedLayers.map(
                        (l) =>
                            l.feature.properties[DYNAMIC_FEATURE_ID_FIELD_NAME]
                    )
                );

                rotateHasSelection = selectedIds.size > 0;

                /* Build the set of allowed IDs for rotation, i.e. every
                 plot that gets a rotation handle. Locked plots never get
                 one. Without a selection every other plot is grabbable
                 and (see onRotateEnd) only the grabbed one rotates; with
                 a selection only the selected plots are grabbable, and
                 they all rotate together.
                */
                rotateAllowedIds = new Set();
                map.eachLayer((layer) => {
                    const featureId =
                        layer.feature?.properties?.[
                            DYNAMIC_FEATURE_ID_FIELD_NAME
                        ];
                    if (featureId === undefined) return;
                    if (lockedIds.has(featureId)) return;
                    if (!rotateHasSelection || selectedIds.has(featureId)) {
                        rotateAllowedIds.add(featureId);
                    }
                });

                // Set pmIgnore on non-allowed layers
                map.eachLayer((layer) => applyRotateIgnore(layer));

                map.pm.enableGlobalRotateMode();
                rotateGuard = false;
            } else {
                clearRotateIgnore();
                if (buttonClickedDuringRotate) {
                    buttonClickedDuringRotate = false;
                } else {
                    map.pm.enableGlobalDragMode();
                }
            }
        });

        // Update GeoJSON state after a polygon is rotated.
        /*
         When rotation was started with an explicit selection, the same
         rotation is co-applied to every other allowed plot (group
         rotate). Otherwise, only the grabbed plot rotates: rotating every
         unlocked plot on the map from a single grab would be too
         error-prone/impractical for CMs, since each plot's row
         orientation is usually independent of its neighbors.
        */
        const onRotateEnd = (e) => {
            parcellaireDispatch({
                type: PARCELLAIRE_REDUCER_ACTIONS.ROTATE,
                featureId:
                    e.layer.feature.properties[DYNAMIC_FEATURE_ID_FIELD_NAME],
                // e.angle is the layer's total angle, only this drag counts
                angle: e.angle - e.startAngle,
                coRotateIds: rotateHasSelection ? rotateAllowedIds : new Set(),
            });
            setGeoJsonKey(new Date().getTime());
        };
        map.on("pm:rotateend", onRotateEnd);

        const toggleSelectAll = () => {
            const currentState = parcellaireStateRef.current;
            if (currentState.selectedLayers.length > 0) {
                parcellaireDispatch({
                    type: PARCELLAIRE_REDUCER_ACTIONS.CHECKPOINT,
                });
            } else {
                const allLayers = map.pm
                    .getGeomanLayers()
                    .filter(
                        (layer) =>
                            layer.feature?.geometry.type !== "Point" &&
                            layer.feature?.properties?.[
                                DYNAMIC_FEATURE_ID_FIELD_NAME
                            ] !== undefined
                    );
                parcellaireDispatch({
                    type: PARCELLAIRE_REDUCER_ACTIONS.SELECT_ALL,
                    allLayers,
                });
            }
            setGeoJsonKey(new Date().getTime());
        };

        map.pm.addControls({
            drawMarker: false,
            drawCircle: false,
            drawCircleMarker: false,
            drawRectangle: false,
            drawPolygon: false,
            drawText: false,
            coordMarkerDraw: false,
            editMode: false,
            dragMode: false,
            cutPolygon: false,
            removalMode: false,
            rotateMode: true,
            // positions: { options: "bottomleft" }, // changes position of specific control block
        });

        map.pm.Toolbar.createCustomControl({
            name: "selectAll",
            block: "edit",
            title: "Select / Unselect all unlocked",
            className: "leaflet-pm-icon-select-all",
            onClick: () => {
                toggleSelectAll();
            },
            afterClick: () => {
                map.pm.enableGlobalDragMode();
            },
            toggle: false,
        });

        map.pm.setGlobalOptions({
            snappable: false,
            pmIgnore: false,
            markerStyle: {
                icon: geomanMarkerIcon,
                interactive: false,
            },
        });

        map.pm.Draw.Line.setPathOptions({
            interactive: false,
            color: "red",
            customType: "marker",
        });

        map.pm.enableGlobalDragMode();

        // Deactivate the linear ruler whenever any geoman toolbar button is clicked.
        // Track whether a button click caused the rotation mode to be disabled,
        // so the rotation handler doesn't force-enable drag mode over the new mode.
        let buttonClickedDuringRotate = false;
        const onButtonClick = (e) => {
            setIsRulerActiveRef.current(false);
            if (isRotatingRef.current && e.btnName !== "rotateMode") {
                buttonClickedDuringRotate = true;
            }
        };
        map.on("pm:buttonclick", onButtonClick);

        if (onSoilDrawRef) {
            onSoilDrawRef.current = {
                enable: () => {
                    // Set the current drawing type and style
                    currentDrawingTypeRef.current = "soil";
                    map.pm.Draw.Polygon.setPathOptions({
                        interactive: true,
                        color: "red",
                    });

                    // Reset the polygon ref and delete layers
                    soilPolygonsRef.current = null;
                    deleteLayers(
                        map,
                        (layer) => layer.options.customType === "soil"
                    );

                    map.pm.enableDraw("Polygon");
                },
                disable: () => {
                    currentDrawingTypeRef.current = null;
                    map.pm.disableDraw();
                    map.pm.enableGlobalDragMode();
                },
            };
        }

        if (onRadioMetricTargetDrawRef) {
            onRadioMetricTargetDrawRef.current = {
                enable: () => {
                    // Set the current drawing type and style
                    currentDrawingTypeRef.current = "radioMetricTarget";
                    map.pm.Draw.Polygon.setPathOptions({
                        interactive: true,
                        color: "magenta",
                    });

                    // Reset the polygon ref and delete layers
                    if (radioMetricTargetPolygonRef) {
                        radioMetricTargetPolygonRef.current = null;
                    }
                    deleteLayers(
                        map,
                        (layer) =>
                            layer.options.customType === "radioMetricTarget"
                    );

                    map.pm.enableDraw("Polygon");
                },
                disable: () => {
                    currentDrawingTypeRef.current = null;
                    map.pm.disableDraw();
                    map.pm.enableGlobalDragMode();
                },
            };
        }

        // Helper to exit all active modes cleanly and restore drag mode
        const exitAllModes = () => {
            map.pm.disableGlobalRotateMode();
            if (map.pm.Draw[POLYGON_SELECT_NAME].enabled()) {
                map.pm.Draw[POLYGON_SELECT_NAME].disable();
            }
            setOpenScaleDialogRef.current(false);
            setIsRulerActiveRef.current(false);
            map.pm.enableGlobalDragMode();
        };

        // Ctrl+Q = polygon select, Ctrl+R = rotation, Ctrl+E = scale, Ctrl+M = ruler
        // All modes are mutually exclusive.
        const onKeyDown = (e) => {
            if (!e.ctrlKey || e.repeat) return;

            // Ignore shortcuts while typing in a field, so e.g. Ctrl+A
            // selects the field's text instead of selecting all plots.
            const activeElement = document.activeElement;
            const isEditingField =
                activeElement &&
                (["INPUT", "TEXTAREA", "SELECT"].includes(
                    activeElement.tagName
                ) ||
                    activeElement.isContentEditable);
            if (isEditingField) return;

            // Ignore shortcuts while a foreign modal (save, export,
            // metadata...) is open. The scale dialog is one of this hook's
            // own mutually-exclusive modes, so its own presence shouldn't
            // block the shortcuts that are meant to switch away from it.
            const openDialogCount =
                document.querySelectorAll('[role="dialog"]').length;
            const isForeignDialogOpen = openScaleDialogRef.current
                ? openDialogCount > 1
                : openDialogCount > 0;
            if (isForeignDialogOpen) return;

            if (e.key === "a") {
                e.preventDefault();
                exitAllModes();
                toggleSelectAll();
            } else if (e.key === "q") {
                e.preventDefault();
                const wasDrawing = map.pm.Draw[POLYGON_SELECT_NAME].enabled();
                exitAllModes();
                if (!wasDrawing) {
                    map.pm.Draw[POLYGON_SELECT_NAME].enable();
                }
            } else if (e.key === "r") {
                e.preventDefault();
                const wasRotating = map.pm.globalRotateModeEnabled();
                exitAllModes();
                if (!wasRotating) {
                    map.pm.enableGlobalRotateMode();
                }
            } else if (e.key === "e") {
                e.preventDefault();
                exitAllModes();
                if (parcellaireStateRef.current.selectedLayers.length > 0) {
                    setOpenScaleDialogRef.current(true);
                }
            } else if (e.key === "m") {
                e.preventDefault();
                const wasRulerActive = isRulerActiveRef.current;
                exitAllModes();
                if (!wasRulerActive) {
                    setIsRulerActiveRef.current(true);
                }
            }
        };
        document.addEventListener("keydown", onKeyDown);

        return () => {
            clearRotateIgnore();
            map.off("layeradd", onLayerAdd);
            document.removeEventListener("keydown", onKeyDown);
            map.pm.removeControls();
            map.pm.setGlobalOptions({ pmIgnore: true, snappable: true });
            map.off("pm:buttonclick", onButtonClick);
            map.off("pm:create", onCreateHandler);
            map.off("pm:globalrotatemodetoggled");
            map.off("pm:rotateend", onRotateEnd);
        };
    }, [
        map,
        parcellaireDispatch,
        reset,
        setCoordMarkerMode,
        setGeoJsonKey,
        setValue,
        onSoilDrawRef,
        setIsDrawingSoil,
        soilPolygonsRef,
        onRadioMetricTargetDrawRef,
        setIsDrawingRadioMetricTarget,
        radioMetricTargetPolygonRef,
        isRotatingRef,
        parcellaireStateRef,
    ]);
};

const POLYGON_SELECT_NAME = "polygonSelect";

const geomanMarkerIcon = L.icon({
    iconUrl: "/static/marker-icon-2x.png",
    shadowUrl: "/static/marker-shadow.png",
    iconSize: L.Icon.Default.prototype.options.iconSize,
    shadowSize: L.Icon.Default.prototype.options.shadowSize,
    iconAnchor: L.Icon.Default.prototype.options.iconAnchor,
    shadowAnchor: L.Icon.Default.prototype.options.shadowAnchor,
    popupAnchor: L.Icon.Default.prototype.options.popupAnchor,
});
